import React from 'react';

export interface SystemHealthBannerProps {
  /** Number of issues detected */
  issueCount: number;
  /** Whether to always show (false = hide when healthy) */
  alwaysShow?: boolean;
  /** Click handler to scroll to issues */
  onViewIssues?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * System Health Banner - shows at-a-glance system status
 *
 * Design: An operator glancing at this should know system health in under 3 seconds.
 * - Green "All Systems Operational" when no issues (optional display)
 * - Amber/Red with issue count when problems exist
 */
export const SystemHealthBanner: React.FC<SystemHealthBannerProps> = ({
  issueCount,
  alwaysShow = false,
  onViewIssues,
  className = '',
}) => {
  const hasIssues = issueCount > 0;

  // Don't render if healthy and alwaysShow is false
  if (!hasIssues && !alwaysShow) {
    return null;
  }

  // Status indicator icon
  const StatusIcon = hasIssues ? (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ) : (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );

  const bannerClasses = hasIssues
    ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'
    : 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800';

  const textClasses = hasIssues
    ? 'text-amber-700 dark:text-amber-300'
    : 'text-emerald-700 dark:text-emerald-300';

  const iconClasses = hasIssues
    ? 'text-amber-500 dark:text-amber-400'
    : 'text-emerald-500 dark:text-emerald-400';

  return (
    <div
      className={`
        flex items-center justify-between gap-3 px-4 py-3
        border rounded-lg
        ${bannerClasses}
        ${className}
      `}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3">
        <span className={iconClasses}>{StatusIcon}</span>
        <span className={`font-medium ${textClasses}`}>
          {hasIssues
            ? `${issueCount} ${issueCount === 1 ? 'Issue' : 'Issues'} Detected`
            : 'All Systems Operational'}
        </span>
      </div>

      {hasIssues && onViewIssues && (
        <button
          onClick={onViewIssues}
          className={`
            text-sm font-medium underline-offset-2 hover:underline
            ${textClasses}
            focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2
            dark:focus:ring-offset-slate-900
          `}
        >
          View Details
        </button>
      )}
    </div>
  );
};

export default SystemHealthBanner;
